import { Transition, Dialog } from '@headlessui/react';
import clsx, { ClassValue } from 'clsx';
import React, { Fragment, useCallback, useState } from 'react';
import { ImageEditor } from '@/components/dashboard';
import styles from './useModalImageEditor.module.scss';

interface ModalImageEditorProps {
	className?: ClassValue;
	classNameCardContainer?: ClassValue;
	children?: React.ReactNode;
	title?: string;
	image: string;
	aspectRatio: number;
	width?: number;
	height?: number;
	hasDownload?: boolean;
	handleImageEdited: (newImage: string, oldImage: string) => void;
	onClose?: () => void;
}
/**
 * Custom hook to return a modal with its handler functions,
 * the returned modal has a default layout
 *
 * @returns
 * 		Modal: Default Modal element that will be rendered
 * 		isOpen: usual modal prop that says when the modal should appear
 * 		show: Handler function to show the modal
 * 		hide: Handler function to hide the modal
 */

const useModalImageEditor = (): {
	ModalImageEditor: React.FC<ModalImageEditorProps>;
	isOpen: boolean;
	showImageEditor: () => void;
	hideImageEditor: () => void;
} => {
	const cancelButtonRef = React.useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	const showImageEditor = (): void => {
		setIsOpen(true);
	};

	const hideImageEditor = (): void => {
		setIsOpen(false);
	};

	const ModalImageEditor: React.FC<ModalImageEditorProps> = useCallback(
		({ ...props }) => (
			<Transition.Root appear show={isOpen} as={Fragment}>
				<Dialog
					as="div"
					className="relative"
					onClose={hideImageEditor}
					static
					initialFocus={cancelButtonRef}
				>
					<div
						className={clsx('z-30 fixed top-0 w-full h-full', props.className)}
					>
						<div className="flex h-full w-full relative  bg-white">
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 scale-95"
								enterTo="opacity-100 scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 scale-100"
								leaveTo="opacity-0 scale-95"
							>
								<Dialog.Panel
									className={clsx(
										'transform transition-all w-full h-full',
										styles.bgDashboardImg,
										props.classNameCardContainer
									)}
								>
									<ImageEditor
										image={props.image}
										aspectRatio={props.aspectRatio}
										handleImageEdited={props.handleImageEdited}
										hideImageEditor={hideImageEditor}
										height={props.height}
										width={props.width}
										hasDownload={props.hasDownload}
									/>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition.Root>
		),
		[isOpen]
	);

	return {
		ModalImageEditor,
		isOpen,
		showImageEditor,
		hideImageEditor,
	};
};

export default useModalImageEditor;
